'use client';

import { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Sphere, Ring, Text } from '@react-three/drei';
import { Project } from '@/types/project';

interface UniverseSystemProps {
  project: Project;
  onClick: () => void;
}

export default function UniverseSystem({ project, onClick }: UniverseSystemProps) {
  const groupRef = useRef<any>(null);
  const sunRef = useRef<any>(null);
  const [hovered, setHovered] = useState(false);
  
  // Animação de rotação mais suave
  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += 0.003;
    }
    
    if (sunRef.current) {
      sunRef.current.rotation.y += 0.01;
      sunRef.current.rotation.x += 0.005;
    }
  });

  // Posições dos planetas (tecnologias) em órbitas mais organizadas
  const planetPositions = project.technologies.slice(1).map((_, index) => {
    const angle = (index / (project.technologies.length - 1)) * Math.PI * 2;
    const radius = 2.5 + index * 0.6;
    return [
      Math.cos(angle) * radius,
      Math.sin(angle * 0.3) * 0.3,
      Math.sin(angle) * radius
    ] as [number, number, number];
  });

  return (
    <group
      ref={groupRef}
      position={project.position}
      onClick={onClick}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      {/* Sol central (tecnologia principal) - estilo monocromático */}
      <Sphere
        ref={sunRef}
        args={[1.2, 32, 32]}
        scale={hovered ? 1.3 : 1}
      >
        <meshStandardMaterial
          color={project.color}
          emissive={project.color}
          emissiveIntensity={hovered ? 0.4 : 0.2}
          roughness={0.2}
          metalness={0.8}
        />
      </Sphere>

      {/* Brilho do sol - mais sutil */}
      <Sphere args={[1.8, 16, 16]}>
        <meshBasicMaterial
          color={project.color}
          transparent
          opacity={hovered ? 0.15 : 0.08}
        />
      </Sphere>

      {/* Órbitas - mais elegantes */}
      {planetPositions.map((_, index) => (
        <Ring
          key={`orbit-${index}`}
          args={[2.5 + index * 0.6 - 0.02, 2.5 + index * 0.6 + 0.02, 64]}
          rotation={[Math.PI / 2, 0, 0]}
        >
          <meshBasicMaterial
            color="#ffffff"
            transparent
            opacity={hovered ? 0.2 : 0.05}
          />
        </Ring>
      ))}

      {/* Planetas (outras tecnologias) - tons de cinza */}
      {planetPositions.map((position, index) => (
        <group key={`planet-${index}`}>
          <Sphere
            position={position}
            args={[0.2 + index * 0.05, 16, 16]}
          >
            <meshStandardMaterial
              color={`hsl(0, 0%, ${80 - index * 15}%)`}
              roughness={0.6}
              metalness={0.4}
              emissive={`hsl(0, 0%, ${20 - index * 3}%)`}
              emissiveIntensity={0.1}
            />
          </Sphere>
          
          {/* Pequeno brilho nos planetas */}
          <Sphere
            position={position}
            args={[0.3 + index * 0.05, 8, 8]}
          >
            <meshBasicMaterial
              color="#ffffff"
              transparent
              opacity={0.05}
            />
          </Sphere>
        </group>
      ))}

      {/* Nome do projeto - mais elegante */}
      {hovered && (
        <Text
          position={[0, 2.5, 0]}
          fontSize={0.6}
          color="white"
          anchorX="center"
          anchorY="middle"
        >
          {project.name}
        </Text>
      )}

      {/* Tecnologia principal - sempre visível */}
      <Text
        position={[0, -2, 0]}
        fontSize={0.3}
        color={project.color}
        anchorX="center"
        anchorY="middle"
      >
        {project.mainTech}
      </Text>

      {/* Partículas flutuantes ao redor do universo */}
      {hovered && [...Array(8)].map((_, i) => (
        <Sphere
          key={`particle-${i}`}
          position={[
            Math.cos(i * 0.785) * 4,
            Math.sin(i * 0.5) * 2,
            Math.sin(i * 0.785) * 4
          ]}
          args={[0.02, 8, 8]}
        >
          <meshBasicMaterial
            color="white"
            transparent
            opacity={0.6}
          />
        </Sphere>
      ))}
    </group>
  );
}